import { useLiveQuery, useStore } from "@hq/client-react";
import {
  normalizeShortcut,
  ShortcutProvider,
  type ShortcutDefinition,
  type ShortcutOverrides,
} from "@z/ui";
import type { ReactNode } from "react";

export const SHORTCUTS_KV_KEY = "keyboard.shortcuts.v1";
export const SHORTCUT_DEFINITIONS: readonly ShortcutDefinition[] = [
  {
    id: "palette.toggle",
    allowInEditor: true,
    title: "Search / command palette",
    group: "General",
    defaultShortcut: "g p",
    scope: "app",
  },
  {
    id: "tasks.new",
    allowInEditor: true,
    title: "Quick capture to Inbox",
    group: "General",
    defaultShortcut: "n t",
    scope: "app",
  },
  { id: "notes.new", title: "New note", group: "Notes", defaultShortcut: "n n", scope: "app" },
  {
    id: "notes.daily",
    title: "Open today's daily note",
    group: "Notes",
    defaultShortcut: "n d",
    scope: "app",
  },
  {
    id: "editor.addTodo",
    title: "Add selection as task",
    group: "Notes",
    defaultShortcut: null,
    scope: "editor",
  },
  {
    id: "editor.dictate",
    title: "Toggle dictation",
    group: "Notes",
    defaultShortcut: null,
    scope: "editor",
  },
  {
    id: "go.today",
    title: "Go to Today",
    group: "Navigation",
    defaultShortcut: "g t",
    scope: "app",
  },
  {
    id: "go.inbox",
    title: "Go to Inbox",
    group: "Navigation",
    defaultShortcut: "g i",
    scope: "app",
  },
  {
    id: "go.anytime",
    title: "Go to Anytime",
    group: "Navigation",
    defaultShortcut: "g a",
    scope: "app",
  },
  {
    id: "go.upcoming",
    title: "Go to Upcoming",
    group: "Navigation",
    defaultShortcut: "g u",
    scope: "app",
  },
  {
    id: "go.notes",
    title: "Go to All Notes",
    group: "Navigation",
    defaultShortcut: "g n",
    scope: "app",
  },
  {
    id: "go.daily",
    title: "Go to Daily Notes",
    group: "Navigation",
    defaultShortcut: "g d",
    scope: "app",
  },
  {
    id: "go.calendar",
    title: "Go to Calendar",
    group: "Navigation",
    defaultShortcut: "g c",
    scope: "app",
  },
  {
    id: "go.settings",
    title: "Open Settings",
    group: "Navigation",
    defaultShortcut: "g s",
    scope: "app",
  },
  {
    id: "layout.pinTab",
    title: "Keep tab open",
    group: "Workspace",
    defaultShortcut: "w p",
    scope: "app",
  },
  {
    id: "layout.closePane",
    title: "Close pane",
    group: "Workspace",
    defaultShortcut: "w x",
    scope: "app",
  },
  {
    id: "layout.nextTab",
    title: "Next tab",
    group: "Workspace",
    defaultShortcut: "w n",
    scope: "app",
  },
  {
    id: "layout.previousTab",
    title: "Previous tab",
    group: "Workspace",
    defaultShortcut: "w b",
    scope: "app",
  },
  ...(["left", "right", "up", "down"] as const).map((direction): ShortcutDefinition => ({
    id: `layout.focus.${direction}`,
    title: `Focus pane ${direction}`,
    group: "Workspace",
    defaultShortcut: `w ${{ left: "h", right: "l", up: "k", down: "j" }[direction]}`,
    scope: "app",
  })),
];

export function readShortcutOverrides(value: unknown): ShortcutOverrides {
  if (!value || typeof value !== "object" || Array.isArray(value)) return {};
  const result: ShortcutOverrides = {};
  for (const definition of SHORTCUT_DEFINITIONS) {
    const binding: unknown = Reflect.get(value, definition.id);
    if (binding === null) result[definition.id] = null;
    else if (typeof binding === "string") {
      try {
        result[definition.id] = normalizeShortcut(binding);
      } catch {
        /* Ignore corrupt preferences. */
      }
    }
  }
  return result;
}

/** Preferences stay in the device-local KV store, alongside the workspace layout. */
export function AppShortcutPreferences({ children }: { children: ReactNode }) {
  const store = useStore();
  const { data } = useLiveQuery(["kv"], (s) => s.kv.get<unknown>(SHORTCUTS_KV_KEY), []);
  return (
    <ShortcutProvider
      definitions={SHORTCUT_DEFINITIONS}
      overrides={readShortcutOverrides(data)}
      save={(overrides) => store.kv.set(SHORTCUTS_KV_KEY, overrides)}
    >
      {children}
    </ShortcutProvider>
  );
}
